import { useState } from 'react';
import {
  classOf,
  declaredLinks,
  entityColumns,
  LINK_KINDS,
  LINK_WORDS,
  linkProblems,
  linkStatement,
  sheetKey,
  sheetName as nameOfSheet,
  sharedIdentifiers,
  sheetOf,
  type FolderLink,
  type LinkKind,
  type SheetState,
} from '@/lib/folderMapping';
import { termFromWords } from '@/lib/columnMapping';
import type { OntologySchema } from '@/lib/useOntology';
import { Message } from '@/components/ui/Message';
import { Button, buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';

/**
 * Page 2 of the several-sheets mapping (ruled 2026-09-30, 4.11): the links
 * between sheets. Five kinds, each a row between an Entity column of one
 * sheet and one of another; a row the report suggested is accepted before it
 * counts, as a column's suggestion is. The sheet panels above show the two
 * rows involved, through `onFocus`.
 */

const pick = 'w-full min-w-28 text-xs';
const small = 'w-full text-xs';

const EMPTY: Omit<FolderLink, 'accepted'> = { kind: 'same_identifier', from: { sheet: '', column: '' }, to: { sheet: '', column: '' } };

export function LinksPage({
  sheets,
  links,
  onChange,
  onFocus,
  schema,
}: {
  sheets: SheetState[];
  links: FolderLink[];
  /** The ontology in the store, so a link takes a declared property before a new one is minted. */
  schema: OntologySchema | null;
  onChange: (next: FolderLink[]) => void;
  /** The two columns the row concerns, each with its sheet. */
  onFocus: (columns: { sheet: string; column: string }[]) => void;
}) {
  const [draft, setDraft] = useState<Omit<FolderLink, 'accepted'>>(EMPTY);
  const [refused, setRefused] = useState<string[] | null>(null);
  // A refusal answers the press that made it; changing the row withdraws it.
  const editDraft = (update: (d: Omit<FolderLink, 'accepted'>) => Omit<FolderLink, 'accepted'>) => {
    setDraft(update);
    setRefused(null);
  };
  const live = sheets.filter((s) => !s.leftOut);
  const shared = sharedIdentifiers(sheets);
  const sheetName = (key: string) => { const s = sheetOf(sheets, key); return s ? nameOfSheet(s) : key; };
  const set = (i: number, patch: Partial<FolderLink>) => onChange(links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i: number) => onChange(links.filter((_, j) => j !== i));
  const draftProblems = linkProblems({ ...draft, accepted: true }, sheets);
  const declared =
    draft.kind === 'object_property' && draft.from.column && draft.to.column
      ? declaredLinks(
          schema,
          classOf(sheetOf(sheets, draft.from.sheet), draft.from.column),
          classOf(sheetOf(sheets, draft.to.sheet), draft.to.column),
        )
      : [];
  const add = () => {
    const problems = [
      ...(draft.from.sheet && draft.from.column ? [] : ['the column it links from (domain)']),
      ...(draft.to.sheet && draft.to.column ? [] : ['the column it links to (range)']),
    ];
    if (problems.length || draftProblems.length) {
      setRefused([...problems.map((p) => `Add needs ${p}.`), ...draftProblems]);
      return;
    }
    const { predicate, ...rest } = draft;
    onChange([...links, { ...rest, ...(predicate ? { predicate: termFromWords(predicate) } : {}), accepted: true }]);
    setDraft(EMPTY);
    setRefused(null);
  };
  const columnSelect = (
    label: string,
    value: { sheet: string; column: string },
    onPick: (v: { sheet: string; column: string }) => void,
    invalid = false,
  ) => (
    <select
      aria-label={label}
      aria-invalid={invalid || undefined}
      value={value.sheet && value.column ? `${value.sheet}\u0000${value.column}` : ''}
      onChange={(e) => {
        const [sheet, column] = e.target.value.split('\u0000');
        onPick({ sheet: sheet ?? '', column: column ?? '' });
      }}
      className={fieldClass(invalid ? 'error' : 'normal', pick)}
    >
      <option value="">sheet and column…</option>
      {live.map((s) => (
        <optgroup key={sheetKey(s)} label={nameOfSheet(s)}>
          {entityColumns(s).map((c) => (
            <option key={c} value={`${sheetKey(s)}\u0000${c}`}>
              {c} ({classOf(s, c)})
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
  const viaSelect = (link: Omit<FolderLink, 'accepted'>, onPick: (via: string) => void, invalid: boolean) => {
    const from = sheetOf(sheets, link.from.sheet);
    if (!from || link.from.sheet === link.to.sheet) return <span className="text-xs text-muted-foreground">same sheet</span>;
    return (
      <select
        aria-label={`Through, for ${link.from.column}`}
        aria-invalid={invalid || undefined}
        value={link.via ?? ''}
        onChange={(e) => onPick(e.target.value)}
        className={fieldClass(invalid ? 'error' : 'normal', pick)}
      >
        <option value="">column holding the identifier…</option>
        {from.columns
          .filter((c) => c !== link.from.column)
          .map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
      </select>
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Page 2 — Links between things</h3>
        <p className="text-xs text-muted-foreground">
          How the things your columns name relate, within one sheet or across sheets. Same identifier: one column's identifiers are
          another's, so a row in each names one thing. Same thing, different identifiers: two columns name one
          thing under two identifiers, and the first is the one the sheets join on. A link (ObjectProperty) or a
          context reaches the other sheet's thing through the column that holds its identifier. Subclass puts
          this column's class under that column's. A suggested row does nothing until accepted.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="tres-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Link</th>
              <th>From <span className="font-normal text-muted-foreground">domain</span></th>
              <th>To <span className="font-normal text-muted-foreground">range</span></th>
              <th>Name, or through</th>
              <th>Declares</th>
            </tr>
          </thead>
          <tbody>
            {shared.map((g) => (
              <tr
                key={`${g.klass}-${g.columns.map((c) => `${c.sheet}:${c.column}`).join('|')}`}
                aria-label={`Same identifier ${g.klass}`}
                onMouseEnter={() => onFocus(g.columns)}
              >
                <td className="text-xs text-muted-foreground">From the columns</td>
                <td className="text-xs">{LINK_WORDS.same_identifier}</td>
                <td className="text-xs" colSpan={3}>
                  {g.columns.map((c) => `${c.column} in ${sheetName(c.sheet)}`).join('; ')}
                </td>
                <td className="font-mono text-xs">one {g.klass} wherever its identifier appears</td>
              </tr>
            ))}
            {links.length === 0 && shared.length === 0 && (
              <tr>
                <td colSpan={6}>
                  {live.length > 1 ? (
                    <Message kind="warning" density="compact">
                      No link between your {live.length} sheets: their entities would be stored as if each sheet stood
                      alone. Add each one below.
                    </Message>
                  ) : (
                    <span className="text-xs text-muted-foreground">No links yet. Add one below when one column's thing relates to another's, such as a player to their team.</span>
                  )}
                </td>
              </tr>
            )}
            {links.map((l, i) => {
              const problems = linkProblems(l, sheets);
              const focus = () => onFocus([l.from, l.to]);
              return (
                <tr key={i} aria-label={`Link ${LINK_WORDS[l.kind]} ${l.from.column} ${l.to.column}`} onMouseEnter={focus} onFocus={focus}>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {!l.accepted && (
                        <Button type="button" variant="secondary" size="sm" onClick={() => set(i, { accepted: true })}>
                          Accept
                        </Button>
                      )}
                      <button type="button" onClick={() => remove(i)} className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                        Remove
                      </button>
                    </div>
                    {!l.accepted && <p className="mt-1 text-xs text-muted-foreground">Suggested{l.reason ? `: ${l.reason}` : ''}</p>}
                  </td>
                  <td className="text-xs">{LINK_WORDS[l.kind]}</td>
                  <td className="font-mono text-xs">
                    {l.from.column} <span className="font-sans text-muted-foreground">{sheetName(l.from.sheet)}</span>
                    {l.kind === 'same_entity' && (
                      <label className="mt-1 flex items-center gap-1 font-sans text-xs">
                        <input type="radio" name={`primary-${i}`} checked={l.primary !== 'to'} onChange={() => set(i, { primary: 'from' })} />
                        primary
                      </label>
                    )}
                  </td>
                  <td className="font-mono text-xs">
                    {l.to.column} <span className="font-sans text-muted-foreground">{sheetName(l.to.sheet)}</span>
                    {l.kind === 'same_entity' && (
                      <label className="mt-1 flex items-center gap-1 font-sans text-xs">
                        <input type="radio" name={`primary-${i}`} checked={l.primary === 'to'} onChange={() => set(i, { primary: 'to' })} />
                        primary
                      </label>
                    )}
                  </td>
                  <td className="min-w-32">
                    {l.kind === 'object_property' && (
                      <input
                        type="text"
                        aria-label={`Property of link ${i + 1}`}
                        placeholder="property"
                        defaultValue={l.predicate ?? ''}
                        onBlur={(e) => set(i, { predicate: termFromWords(e.target.value) })}
                        className={fieldClass(l.predicate?.trim() ? 'normal' : 'error', `${small} font-mono`)}
                      />
                    )}
                    {(l.kind === 'object_property' || l.kind === 'context') &&
                      viaSelect(l, (via) => set(i, { via }), l.from.sheet !== l.to.sheet && !l.via)}
                  </td>
                  <td>
                    {problems.length > 0 && l.accepted ? (
                      <Message kind="warning" density="compact">
                        {problems.join(' ')}
                      </Message>
                    ) : (
                      <span className="font-mono text-xs">{linkStatement(l, sheets)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr onMouseEnter={() => onFocus([draft.from, draft.to].filter((c) => c.sheet && c.column))}>
              <td>
                <Button type="button" variant="secondary" size="sm" onClick={add}>
                  Add
                </Button>
              </td>
              <td>
                <select
                  aria-label="New link: kind"
                  value={draft.kind}
                  onChange={(e) => editDraft((d) => ({ ...d, kind: e.target.value as LinkKind }))}
                  className={fieldClass('normal', pick)}
                >
                  {LINK_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {LINK_WORDS[k]}
                    </option>
                  ))}
                </select>
              </td>
              <td>{columnSelect('New link: domain', draft.from, (from) => editDraft((d) => ({ ...d, from })), Boolean(refused) && !draft.from.column)}</td>
              <td>{columnSelect('New link: range', draft.to, (to) => editDraft((d) => ({ ...d, to })), Boolean(refused) && !draft.to.column)}</td>
              <td className="min-w-32">
                {declared.length > 0 && (
                  <div className="mb-1 space-y-1">
                    <p className="text-xs text-muted-foreground">Declared in your ontology:</p>
                    {declared.map((d) => (
                      <Button
                        key={`${d.uri}-${d.reversed}`}
                        type="button"
                        variant="secondary"
                        size="sm"
                        aria-pressed={draft.predicate === d.uri}
                        onClick={() =>
                          editDraft((row) => {
                            if (!d.reversed) return { ...row, predicate: d.uri };
                            // The ends swap, so a through column of the old
                            // domain's sheet no longer applies.
                            const next = { ...row, from: row.to, to: row.from, predicate: d.uri };
                            delete next.via;
                            return next;
                          })
                        }
                      >
                        {d.label ?? d.uri}
                        {d.reversed && ` (${draft.to.column} to ${draft.from.column})`}
                      </Button>
                    ))}
                  </div>
                )}
                {draft.kind === 'object_property' && (
                  <input
                    type="text"
                    aria-label="New link: property"
                    placeholder={declared.length > 0 ? 'or a new property' : 'property'}
                    value={draft.predicate ?? ''}
                    onChange={(e) => editDraft((d) => ({ ...d, predicate: e.target.value }))}
                    className={fieldClass('normal', `${small} font-mono`)}
                  />
                )}
                {(draft.kind === 'object_property' || draft.kind === 'context') && draft.from.sheet && draft.to.sheet &&
                  viaSelect(draft, (via) => editDraft((d) => ({ ...d, via })), false)}
                {draft.kind === 'same_entity' && draft.from.column && draft.to.column && (
                  <select
                    aria-label="New link: primary"
                    value={draft.primary ?? 'from'}
                    onChange={(e) => editDraft((d) => ({ ...d, primary: e.target.value as 'from' | 'to' }))}
                    className={fieldClass('normal', pick)}
                  >
                    <option value="from">primary: {draft.from.column}</option>
                    <option value="to">primary: {draft.to.column}</option>
                  </select>
                )}
              </td>
              <td className="font-mono text-xs text-muted-foreground">
                {draft.from.column && draft.to.column ? linkStatement({ ...draft, accepted: true }, sheets) : ''}
              </td>
            </tr>
            {refused && (
              <tr>
                <td colSpan={6}>
                  <Message kind="warning" density="compact">
                    Nothing was added. {refused.join(' ')}
                  </Message>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
